import React from 'react';

import { CHEVRON_DOWN, CHEVRON_RIGHT } from '../icons';
import { formatCount } from '../../utils/counts';

/**
 * THE group header: a micro-label, a 1px rule filling the row, and the
 * group's count at the right. The items list's day groups, the collections
 * index's sections, and the cards library's scope groups all render this
 * one row, so a group reads the same on every list.
 *
 *   <GroupHeader label="Today · Aug 28" count={12} sticky />
 *   <GroupHeader label="My collections" count={39} countLabel="collection" />
 *   <GroupHeader label="No date" count={41} collapsible collapsed onToggle={fn} />
 *
 * `count` alone renders the bare number; with `countLabel` it renders as
 * words ("39 collections"). Zero renders nothing (zero is not information).
 * `sticky` pins the header beneath the pinned shell header (`--shell-pin-offset`;
 * the top of the window on a phone) inside a scrolling feed (KPR-1246).
 *
 * `collapsible` makes the WHOLE row the button — a chevron before the
 * micro-label, `aria-expanded` on the control itself — and the count stays
 * visible, so a collapsed "NO DATE · 41 items" still says what it hides.
 * `onToggle` receives the click event, so a caller can read `shiftKey`.
 */
export default function GroupHeader({
	label,
	count,
	countLabel,
	action = null,
	sticky = false,
	className = '',
	as: Tag = 'div',
	collapsible = false,
	collapsed = false,
	onToggle,
	...rest
}) {
	let countNode = null;
	if (typeof count === 'number') {
		countNode = countLabel ? formatCount(count, countLabel) : (count > 0 ? count : null);
	} else if (count) {
		countNode = count;
	}
	const classes = ['group-header', sticky ? 'group-header--sticky' : '', className].filter(Boolean).join(' ');
	const body = (
		<>
			<span className="micro-label group-header__label">{label}</span>
			<span className="group-header__rule" aria-hidden="true" />
			{countNode !== null ? <span className="group-header__count">{countNode}</span> : null}
		</>
	);

	if (collapsible) {
		const Chevron = collapsed ? CHEVRON_RIGHT : CHEVRON_DOWN;
		const button = (
			<button
				type="button"
				className={`${classes} group-header--collapsible`}
				aria-expanded={!collapsed}
				onClick={onToggle}
				{...rest}
			>
				<Chevron className="group-header__chevron" aria-hidden="true" />
				{body}
			</button>
		);
		// An interactive `action` cannot live inside the button; the two
		// share a row instead. (No caller needs both today.)
		return action ? <div className="group-header__with-action">{button}{action}</div> : button;
	}

	return (
		<Tag className={classes} {...rest}>
			{body}
			{action}
		</Tag>
	);
}
